import { Component, OnInit, inject, signal, computed } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { forkJoin } from 'rxjs';
import { environment } from '../../../../../environments/environment';
import { AuthFacade } from '../../../../Facades/auth.facade';
import { OnboardingFacade } from '../../../../Facades/onboarding.facade';
import { OnboardingStatus } from '../../../../Interfaces/enums';
import { AuthStep } from '../../Interfaces/auth-step.interface';

@Component({
  selector: 'app-callback',
  standalone: true,
  imports: [
    CommonModule,
    MatProgressBarModule,
    MatProgressSpinnerModule,
    MatButtonModule,
    MatIconModule,
    TranslateModule,
  ],
  templateUrl: './callback.component.html',
  styleUrls: ['./callback.component.scss']
})
export class CallbackComponent implements OnInit {
  private readonly _route = inject(ActivatedRoute);
  private readonly _router = inject(Router);
  private readonly _auth = inject(AuthFacade);
  private readonly _onboarding = inject(OnboardingFacade);
  private readonly _translate = inject(TranslateService);

  readonly steps = signal<AuthStep[]>(this.buildSteps());

  readonly errorDetail = signal<string | null>(null);

  readonly progress = computed(() => {
    const allSteps = this.steps();
    const total = allSteps.length;
    const completed = allSteps.filter(s => s.state === 'completed').length;
    const isLoading = allSteps.some(s => s.state === 'loading');
    const score = completed + (isLoading ? 0.5 : 0);
    return (score / total) * 100;
  });

  readonly activeMessage = computed(() => {
    const allSteps = this.steps();

    if (this.errorDetail()) return 'onboarding.auth.callback.authFailed';

    const activeStep = allSteps.find(s => s.state === 'loading')
      || allSteps.find(s => s.state === 'pending');

    return activeStep ? activeStep.label : 'onboarding.auth.callback.redirecting';
  });

  readonly hasError = computed(() =>
    this.steps().some(step => step.state === 'error')
  );

  ngOnInit(): void {
    this.executeAuthSequence();
  }

  private async executeAuthSequence(): Promise<void> {
    this.updateStep('exchange', 'loading');
    await this.delay(600);

    const code = this._route.snapshot.queryParamMap.get('code');
    const errorMsg = this._route.snapshot.queryParamMap.get('error');

    if (errorMsg) {
      this.failStep('exchange', decodeURIComponent(errorMsg));
      return;
    }

    if (!code) {
      this.failStep('exchange', this._translate.instant('onboarding.auth.callback.errors.codeMissing'));
      return;
    }

    this._auth.exchangeCode(code).subscribe({
      next: () => {
        this.updateStep('exchange', 'completed');
        this.handlePostExchangeSequence();
      },
      error: (err: HttpErrorResponse) => {
        const apiMessage = err?.error?.message;
        this.failStep('exchange', apiMessage || this._translate.instant('onboarding.auth.callback.errors.exchangeFailed'));
      }
    });
  }

  private handlePostExchangeSequence(): void {
    this.updateStep('session', 'loading');

    this._onboarding.createSession().subscribe({
      next: () => {
        this.updateStep('session', 'completed');
        this.prefetchWorkspace();
      },
      error: (err: HttpErrorResponse) => {
        if (err.status === 409) {
          this.handOffToApp();
          return;
        }

        const apiMessage = err?.error?.message;
        this.failStep('session', apiMessage || this._translate.instant('onboarding.auth.callback.errors.sessionFailed'));
      }
    });
  }

  private handOffToApp(): void {
    this.markAllStepsComplete();

    this._auth.terminateSession().subscribe({
      next: () => this.redirectToApp(),
      error: () => this.redirectToApp()
    });
  }

  private redirectToApp(): void {
    window.location.replace(environment.appURL);
  }

  private markAllStepsComplete(): void {
    this.steps.update(current =>
      current.map(step => ({ ...step, state: 'completed' as const }))
    );
  }

  private prefetchWorkspace(): void {
    this.updateStep('workspace', 'loading');

    forkJoin({
      config: this._onboarding.getOnboardingConfig({ skipLoading: true }),
      onboarding: this._onboarding.get({ skipLoading: true }),
    }).subscribe({
      next: () => {
        this.updateStep('workspace', 'completed');
        this.handleRedirect();
      },
      error: (err: HttpErrorResponse) => {
        const apiMessage = err?.error?.message;
        this.failStep('workspace', apiMessage || this._translate.instant('onboarding.auth.callback.errors.workspaceFailed'));
      }
    });
  }

  private async handleRedirect(): Promise<void> {
    this.updateStep('redirect', 'loading');
    await this.delay(1200);

    const returnUrl = this._auth.getAndClearReturnUrl();
    this.updateStep('redirect', 'completed');

    const target = returnUrl === '/setup'
      ? this.resolveResumeUrl(this._onboarding.onboarding()?.status)
      : returnUrl;

    this._router.navigateByUrl(target);
  }

  private resolveResumeUrl(status: OnboardingStatus | null | undefined): string {
    switch (status) {
      case OnboardingStatus.COMPANY_SAVED:
      case OnboardingStatus.PLAN_SELECTED:
        return '/setup/plan-selection';
      case OnboardingStatus.PAYMENT_PENDING:
        return '/setup/review';
      case OnboardingStatus.PAYMENT_CONFIRMED:
      case OnboardingStatus.CONSENT_GRANTED:
        return '/setup/connect-intune';
      case OnboardingStatus.COMPLETED:
        return '/setup';
      default:
        return '/setup/company-setup';
    }
  }

  private updateStep(id: string, state: 'pending' | 'loading' | 'completed' | 'error'): void {
    this.steps.update(current =>
      current.map(step => step.id === id ? { ...step, state } : step)
    );
  }

  private failStep(id: string, message: string): void {
    this.updateStep(id, 'error');
    this.errorDetail.set(message);
  }

  returnToLogin(): void {
    this._router.navigate(['/login']);
  }

  retryAuth(): void {
    this._auth.clearAuth();
    this.errorDetail.set(null);
    this.steps.set(this.buildSteps());
    this.executeAuthSequence();
  }

  private buildSteps(): AuthStep[] {
    return [
      { id: 'exchange', label: 'onboarding.auth.callback.steps.exchange', state: 'pending' },
      { id: 'session', label: 'onboarding.auth.callback.steps.session', state: 'pending' },
      { id: 'workspace', label: 'onboarding.auth.callback.steps.workspace', state: 'pending' },
      { id: 'redirect', label: 'onboarding.auth.callback.steps.redirect', state: 'pending' },
    ];
  }

  private delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }
}
